import { useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY = 3000;

export type UseMouseOverWithDelayedExitProps = {
	delay?: number;
	initiallyHovered?: boolean;
	// if this value changes, the component will reset the mouse over state
	resetKey?: string;
};

export const useMouseOverWithDelayedExit = ({
	delay = DEFAULT_DELAY,
	initiallyHovered = false,
	resetKey,
}: UseMouseOverWithDelayedExitProps = {}) => {
	const [isMouseOver, setIsMouseOver] = useState(initiallyHovered);
	const hasInteractedRef = useRef(false);
	const timerRef = useRef<number | null>(null);

	const handleMouseEnter = () => {
		if (!hasInteractedRef.current) {
			hasInteractedRef.current = true;
		}
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		setIsMouseOver(true);
	};

	const handleMouseLeave = () => {
		if (!hasInteractedRef.current) {
			return;
		}
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
		}
		timerRef.current = window.setTimeout(() => setIsMouseOver(false), delay);
	};

	useEffect(() => {
		hasInteractedRef.current = false;
		setIsMouseOver(initiallyHovered);
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, [resetKey, initiallyHovered]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				window.clearTimeout(timerRef.current);
			}
		};
	}, []);

	return { isMouseOver, handleMouseEnter, handleMouseLeave };
};
